import {
  AlarmGroupsIcon,
  AuditLogIcon,
  DesktopFrameIcon,
  DoorGrpIcon,
  DoorsIcon,
  InsightsIcon,
  LocationIcon,
  ScanIcon,
  UnifiedMonitoringIcon,
  UserIcon,
} from '@hakimo-ui/hakimo/ui-elements';
import {
  eventTracker,
  pageTitleAtom,
  showRootHeaderAtom,
} from '@hakimo-ui/hakimo/util';
import { Button } from '@hakimo-ui/shared/ui-base';
import {
  ArrowLeftIcon,
  ArrowsPointingInIcon,
  BellAlertIcon,
  BoltIcon,
  Cog6ToothIcon,
  DocumentTextIcon,
  MagnifyingGlassIcon,
  SpeakerWaveIcon,
  VideoCameraIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { useSetAtom } from 'jotai';
import { ReactNode, useEffect, useMemo } from 'react';

interface Props {
  title: string;
  subtitle?: ReactNode;
  children: ReactNode;
  secondary?: ReactNode;
  pageActions?: ReactNode[];
  onClickBack?: () => void;
  titleFontSize?: 'small' | 'large';
  contentClassName?: string;
}

const titleFontSizeMap: Record<'small' | 'large', string> = {
  small: 'text-sm',
  large: 'text-lg',
};

const iconMap: Record<string, React.ElementType> = {
  Monitoring: DesktopFrameIcon,
  Alarms: BellAlertIcon,
  'Location Alarms': ArrowsPointingInIcon,
  'Alarm Groups': AlarmGroupsIcon,
  Doors: DoorsIcon,
  Cameras: VideoCameraIcon,
  'Audio Devices': SpeakerWaveIcon,
  Locations: LocationIcon,
  'Door Groups': DoorGrpIcon,
  'Alarm Type Groups': AlarmGroupsIcon,
  Users: UserIcon,
  'Audit Log': AuditLogIcon,
  Insights: InsightsIcon,
  Scan: ScanIcon,
  'Scan Escalations': BoltIcon,
  'Unified Monitoring': UnifiedMonitoringIcon,
  Settings: Cog6ToothIcon,
  Reports: DocumentTextIcon,
  'Incident Details': DocumentTextIcon,
  'Forensic Search': MagnifyingGlassIcon,
};

export function Page(props: Props) {
  const {
    title,
    subtitle,
    children,
    secondary,
    pageActions,
    // fullWidth = false,
    onClickBack,
    titleFontSize = 'small',
    contentClassName = 'bg-onlight-bg-1 dark:bg-ondark-bg-1 rounded-t-lg',
  } = props;

  const setTitleNode = useSetAtom(pageTitleAtom);
  const setShowRootHeader = useSetAtom(showRootHeaderAtom);

  useEffect(() => {
    setShowRootHeader(true);
    return () => setShowRootHeader(true);
  }, [setShowRootHeader]);

  const titleNode = useMemo(() => {
    const Icon = iconMap[title];
    return (
      <div className="flex items-center space-x-2">
        {onClickBack && (
          <Button
            variant="icon"
            onClick={onClickBack}
            onSideEffect={eventTracker('navigate_back_page')}
          >
            <ArrowLeftIcon className="w-5" />
          </Button>
        )}
        {Icon && <Icon className="dark:text-dark-text h-6 w-6 text-gray-800" />}
        <div>
          <h1
            className={clsx(
              'dark:text-dark-text text-gray-800',
              titleFontSizeMap[titleFontSize]
            )}
          >
            {title}
          </h1>
          {subtitle && (
            <h2 className="text-xs font-light text-gray-600 dark:text-gray-300">
              {subtitle}
            </h2>
          )}
        </div>
      </div>
    );
  }, [onClickBack, subtitle, title, titleFontSize]);

  useEffect(() => {
    setTitleNode({
      pageTitleNode: titleNode,
      pageActions,
    });
  }, [setTitleNode, titleNode, pageActions]);

  return (
    <main className="bg-hakimo-light-bg dark:bg-hakimo-dark-bg dark:text-dark-text h-full overflow-y-auto">
      <div className="h-full px-4 pt-2">
        <div className="flex h-full flex-col">
          {secondary && (
            <div className="mb-2 flex justify-end">{secondary}</div>
          )}
          <div className={`flex min-h-0 flex-1 flex-col ${contentClassName}`}>
            {children}
          </div>
        </div>
      </div>
    </main>
  );
}

export default Page;
